// Sun-altitude → day factor. Pure math, RN-portable (no DOM). Same NOAA/SunCalc
// machinery that drives the sun-arc; here we expose the live ALTITUDE so the home
// background can crossfade blue (day) ↔ almost-black purple (night) through dusk/dawn.

const rad = Math.PI / 180
const dayMs = 86400000
const J1970 = 2440588
const J2000 = 2451545
const e = rad * 23.4397 // obliquity of the ecliptic

const toDays = (d: Date) => d.valueOf() / dayMs - 0.5 + J1970 - J2000
const solarMeanAnomaly = (d: number) => rad * (357.5291 + 0.98560028 * d)
const eclipticLongitude = (M: number) =>
  M +
  rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) +
  rad * 102.9372 +
  Math.PI
const declination = (l: number) => Math.asin(Math.sin(e) * Math.sin(l))
const rightAscension = (l: number) => Math.atan2(Math.sin(l) * Math.cos(e), Math.cos(l))
const siderealTime = (d: number, lw: number) => rad * (280.16 + 360.9856235 * d) - lw

/** Sun altitude in degrees above the horizon at lat/lon for the given instant. */
export function sunAltitudeDeg(date: Date, lat: number, lon: number): number {
  const lw = rad * -lon
  const phi = rad * lat
  const d = toDays(date)
  const M = solarMeanAnomaly(d)
  const L = eclipticLongitude(M)
  const dec = declination(L)
  const H = siderealTime(d, lw) - rightAscension(L)
  const alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H))
  return alt / rad
}

/**
 * Day blend in [0, 1] from the sun's altitude:
 *   alt ≥ +6° → 1 (full day),  alt ≤ −6° → 0 (full night),  linear ramp between.
 * The home background crossfades the day-blue layer over the night-purple base by this
 * factor, so dusk/dawn glide blue ↔ purple as the sun crosses the ±6° civil band.
 */
export function dayFactor(altDeg: number): number {
  if (altDeg >= 6) return 1
  if (altDeg <= -6) return 0
  return (altDeg + 6) / 12
}
